import { LevelPopupBoosterLayout } from '../../../../Layouts/LevelPopup/LevelPopupBoosterLayout';
import { PreBoosterType } from '../../../../State/Card/BoosterType';
import SpriteComponent from '../../../../Component/Render/Sprite';
import Game from '../../../../Game';
import TextComponent from '../../../../Component/Render/Text';
import GameObject, { GameObjectEvent } from '../../../../GameObject/GameObject';
import Renderable from '../../../../Component/Renderable';
import Interactable from '../../../../Component/Interactable';
import Signal from '../../../../Utility/Signal';
import Armature from '../../../../Prototype/Armature';
import { Anim, StartAnimationClip } from '../../../Animation/AnimationHelper';
import { DragonbonesDefault } from '../../../Animation/Clip';
import { Easing } from '@tweenjs/tween.js';
import { sleep } from '../../../../Utility/async';
import { Layout } from '../../../../Utility/layout/createLayout';

const bgByPreBoosterType: Record<PreBoosterType, string> = {
    [PreBoosterType.REMOVE_THREE]: 'booster_remove_bg',
    [PreBoosterType.JOKER]: 'booster_joker_bg',
    [PreBoosterType.CANISTER]: 'booster_canister_bg',
};

export const iconByPreBoosterType: Record<PreBoosterType, string> = {
    [PreBoosterType.REMOVE_THREE]: 'booster_remove',
    [PreBoosterType.JOKER]: 'booster_joker',
    [PreBoosterType.CANISTER]: 'booster_canister',
};

const lockedIconByPreBoosterType: Record<PreBoosterType, string> = {
    [PreBoosterType.REMOVE_THREE]: 'booster_remove_unlock',
    [PreBoosterType.JOKER]: 'booster_joker_unlock',
    [PreBoosterType.CANISTER]: 'booster_canister_unlock',
};

export const unlockLevelByPreBoosterType: Record<PreBoosterType, number> = {
    [PreBoosterType.REMOVE_THREE]: 30,
    [PreBoosterType.JOKER]: 50,
    [PreBoosterType.CANISTER]: 70,
};

type LevelPopupBoosterProps = {
    type: PreBoosterType;
    justBought: boolean;
};

export class LevelPopupBooster {
    public readonly type: PreBoosterType;
    public readonly onClick: Signal;
    private readonly layout: any;
    private readonly checkmark: Armature;
    private justBought: boolean;
    private selected: boolean = false;
    private prevSelected: boolean = false;
    private sparkles: Armature;

    constructor(props: LevelPopupBoosterProps) {
        this.type = props.type;
        this.justBought = props.justBought;
        this.onClick = new Signal();

        this.layout = new LevelPopupBoosterLayout();
        this.layout.iconBg.getComponent(SpriteComponent).texture = bgByPreBoosterType[this.type];

        if(!this.isLocked()) {
            this.checkmark = GameObject.Instantiate(Armature, {
                dragonbonesName: 'prelvl_booster_icon_check',
                armatureName: 'Armature',
                playOnStart: false,
            });
            this.checkmark.timeScale = 1.5;
            this.checkmark.getComponent(Renderable).alpha = 0;
            this.layout.checkmarkContainer.addChild(this.checkmark);

            this.sparkles = GameObject.Instantiate(Armature, {
                dragonbonesName: 'prelvl_booster_icon_sparkles',
                armatureName: 'Armature',
                playOnStart: false,
            });
            this.sparkles.getComponent(Renderable).alpha = 0;
            this.layout.sparklesContainer.addChild(this.sparkles);
        }

        this.layout.bg.getComponent(Interactable).toggleable = false;
        this.layout.bg.on(GameObjectEvent.SELECTED, this.handleClick);
    }

    private handleClick = (): void => {
        this.onClick.dispatch();
    };

    public get root(): GameObject {
        return this.layout.root;
    }

    public get sparklesContainer(): GameObject {
        return this.layout.sparklesContainer;
    }

    public getSparkles(): Armature {
        return this.sparkles;
    }

    public set isSelected(value: boolean) {
        this.selected = value;
    }

    public get isSelected(): boolean {
        return this.selected;
    }

    public isLocked(): boolean {
        // when reached level is equal booster unlock level
        return Game.player.getHighestLevelCompleted() + 1 < unlockLevelByPreBoosterType[this.type];
    }

    public getAmount(): number {
        const state = Game.store.getState();
        return state.playerState.inventory[this.type];
    }

    public update(): void {
        const unlockLevel = unlockLevelByPreBoosterType[this.type];
        const levelLabel = Game.locale.getText('Level').replace('[x]', unlockLevel.toString());
        const locked = this.isLocked();
        const amount = this.getAmount();

        this.layout.levelText.getComponent(TextComponent).text = levelLabel;
        this.layout.amountText.getComponent(TextComponent).text = `${amount}`;
        this.layout.amountBg.getComponent(SpriteComponent).texture =
            amount > 0 ? 'booster_btn_counter' : 'booster_btn_purchase';

        let bgAsset = this.selected ? 'booster_active' : 'booster_frame';
        bgAsset = locked ? 'booster_locked' : bgAsset;
        this.layout.bg.getComponent(SpriteComponent).texture = bgAsset;

        this.layout.icon.getComponent(SpriteComponent).texture =
            locked ? lockedIconByPreBoosterType[this.type] : iconByPreBoosterType[this.type];

        this.layout.lockBg.getComponent(Renderable).visible = locked;
        this.layout.amountBg.getComponent(Renderable).visible = !locked;
        this.layout.amountText.getComponent(Renderable).visible = amount > 0;
        this.layout.iconBg.getComponent(Renderable).visible = !locked;
        this.playAnimations(locked);

        this.prevSelected = this.selected;
    }

    private async playAnimations(locked: boolean): Promise<void> {
        this.setInputEnabled(false);

        if (this.selected && !this.prevSelected) {
            await this.selectAnimation();
        }

        if (!this.selected && this.prevSelected) {
            await this.unselectAnimation();
        }

        this.setInputEnabled(!locked);
    }

    private async selectAnimation(): Promise<void> {
        if (this.justBought) {
            this.justBought = false;
            StartAnimationClip(new DragonbonesDefault(this.sparkles, { animationName: 'animtion0', playTimes: 1 }));
            await this.sleep(700);
            await Anim(this.sparkles, { alpha: 1, duration: 500 });
        }

        Anim(this.layout.amountBg, { scaleX: 0, scaleY: 0, duration: 300, easing: Easing.Sinusoidal.In });

        Anim(this.sparkles, { alpha: 0, duration: 500 });
        await Anim(this.checkmark, { alpha: 0, duration: 20 });
        StartAnimationClip(new DragonbonesDefault(this.checkmark, { animationName: 'animtion0', playTimes: 1 }));
        await this.sleep(20);

        if (this.checkmark.hasComponent(Renderable)) {
            this.checkmark.getComponent(Renderable).alpha = 1;
        }
    }

    private async unselectAnimation(): Promise<void> {
        Anim(this.checkmark, { alpha: 0, duration: 300 });
        await Anim(this.layout.amountBg, { scaleX: 1, scaleY: 1, duration: 300, easing: Easing.Sinusoidal.Out });
    }

    private setInputEnabled(value: boolean): void {
        this.layout.bg.getComponent(Interactable).enabled = value;
        this.layout.bg.getComponent(Interactable).buttonMode = value;
    }

    private async sleep(duration: number): Promise<void> {
        await sleep(duration);

        if (this.layout.root.destroyed) {
            await new Promise(() => void 0);
        }
    }
}
